/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useDeletePayment } from '@hooks/accommodation';
import { Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  ACCOMMODATION_PAYMENT_METHODS,
  AccommodationBooking,
  AccommodationPayment,
} from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';
import { FinancialEditReasonField } from './FinancialEditReasonField';

interface DeletePaymentModalProps {
  isOpen: boolean;
  payment: AccommodationPayment | null;
  booking: AccommodationBooking;
  onClose: () => void;
}

export const DeletePaymentModal: React.FC<DeletePaymentModalProps> = ({
  isOpen,
  payment,
  booking,
  onClose,
}) => {
  const deletePayment = useDeletePayment();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen || !payment) return null;

  const method = ACCOMMODATION_PAYMENT_METHODS.find(
    (m) => m.id === payment.payment_method_id,
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      apiError('Enter a reason for deleting this payment.');
      return;
    }

    deletePayment.mutate(
      { paymentId: payment.id, reason: reason.trim() },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Delete payment
            </h3>
            <p className="text-xs text-gray-400">
              Booking #{booking.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="rounded-xl border border-red-100 bg-red-50/60 px-3.5 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {booking.currency} {payment.amount.toLocaleString()}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {method?.name ?? `Method ${payment.payment_method_id}`} ·{' '}
              {new Date(payment.paid_at).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
              {payment.notes ? ` · ${payment.notes}` : ''}
            </p>
          </div>

          <FinancialEditReasonField
            id="dp-reason"
            value={reason}
            onChange={setReason}
          />

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={deletePayment.isPending || !reason.trim()}
              className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              <Trash2 size={14} />
              {deletePayment.isPending ? 'Deleting…' : 'Delete payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
